/**
 * 저장 debounce 스케줄러 (일감 G4/S4a) — persist() 다발 호출을 윈도로 모아 직렬화+쓰기를 1회로.
 *
 * 커맨드(구매/배치/환생 등)마다 즉시 저장하면 BigNumber 직렬화 + 어댑터 쓰기가 연타 구간(소탕 연타,
 * 드래그 배치)에 중복 실행된다. RefreshScheduler(G3)와 동형 — 타이머는 주입(scene.time 또는 setTimeout),
 * scene 셧다운 시 cancel()로 정리.
 *
 * 즉시성이 필요한 지점(pagehide/visibility hidden)은 flushNow()로 윈도를 우회한다.
 * canSave 가드: 저장 로드(loadAndApply) 완료 전 트리거를 무시해 초기 빈 상태가 실제 저장을 덮어쓰는 사고 방지.
 */
export interface PersistSchedulerDeps {
  /** 실제 저장 — 직렬화 + 어댑터 쓰기 (GameScene toSaveData→saveSystem.save). */
  save: () => void;
  /** 타이머 주입 — 반환값은 취소 함수 (scene.time.delayedCall 또는 setTimeout 래퍼). */
  schedule: (cb: () => void, delayMs: number) => () => void;
  /** debounce 윈도 만료 후 실제 저장을 idle 구간으로 넘긴다. 미주입 시 기존처럼 즉시 저장. */
  deferSave?: (cb: () => void) => () => void;
  /** debounce 윈도 ms (기본 800 — 연타 묶음과 유실 윈도의 절충). */
  windowMs?: number;
  /** 저장 가능 가드 — false면 request 무시 (로드 완료 전 빈 상태 저장 방지). 기본 항상 true. */
  canSave?: () => boolean;
}

export class PersistScheduler {
  private cancelPending: (() => void) | null = null;
  private cancelDeferred: (() => void) | null = null;

  constructor(private readonly deps: PersistSchedulerDeps) {}

  /** 저장 요청 — 윈도 내 다발 요청은 1회 저장으로 합쳐진다. */
  request(): void {
    if (!(this.deps.canSave?.() ?? true)) return;
    if (this.cancelPending) return; // 이미 예약됨 — 윈도 만료 시 1회 저장
    this.cancelPending = this.deps.schedule(() => {
      this.cancelPending = null;
      if (this.deps.deferSave) {
        this.cancelDeferred = this.deps.deferSave(() => {
          this.cancelDeferred = null;
          if (this.deps.canSave?.() ?? true) this.deps.save();
        });
      } else {
        this.deps.save();
      }
    }, this.deps.windowMs ?? 800);
  }

  /** 즉시 저장 — pagehide/visibility hidden 등 유실 위험 지점. 예약분은 취소(중복 저장 방지). */
  flushNow(): void {
    if (!(this.deps.canSave?.() ?? true)) return;
    this.cancel();
    this.deps.save();
  }

  /** 예약 취소 — scene 셧다운/저장 초기화(resetSave) 직전 호출 (clear 후 pending 재저장 부활 방지). */
  cancel(): void {
    this.cancelPending?.();
    this.cancelPending = null;
    this.cancelDeferred?.();
    this.cancelDeferred = null;
  }
}
